import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import ConfirmModal from '../UI/ConfirmModal';
import {
  monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, ghostBtn, ghostBtnStyle,
  inputCls, inputStyle, fieldLabel, fieldLabelStyle, errMessage,
} from '../UI/ink';

const Label = ({ children }) => <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>;
const promptCls = 'box-border w-full px-3.5 py-3 rounded-[10px] border font-mono text-[11.5px] leading-relaxed outline-none focus:border-[color:var(--accent)]';

const EMPTY = { name: '', specialization: '', role: '', methodology: '', tools: '', prompt: '' };

const AgentForm = ({ initial, skills, onCancel, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const field = (k) => ({ value: form[k], onChange: e => setForm(f => ({ ...f, [k]: e.target.value })) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/workflows/agents', {
        name: form.name.trim(),
        specialization: form.specialization.trim() || undefined,
        role: form.role.trim() || undefined,
        methodology: form.methodology.trim() || undefined,
        tools: form.tools.split(',').map(t => t.trim()).filter(Boolean),
        prompt: form.prompt.trim() || undefined,
      });
      onSaved(res.data?.id);
    } catch (err) {
      setError(errMessage(err, 'Could not create the agent.'));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
      <label className="flex flex-col gap-1.5"><span className={fieldLabel} style={fieldLabelStyle}>Name</span><input {...field('name')} required placeholder="Web Tier Reviewer" className={inputCls} style={inputStyle} /></label>
      <label className="flex flex-col gap-1.5"><span className={fieldLabel} style={fieldLabelStyle}>What it specialises in</span><input {...field('specialization')} placeholder="Checks web-server findings against deploy notes" className={inputCls} style={inputStyle} /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5"><span className={fieldLabel} style={fieldLabelStyle}>Role <span style={{ color: 'var(--ink-faint)' }}>(optional)</span></span><input {...field('role')} className={inputCls} style={inputStyle} /></label>
        <label className="flex flex-col gap-1.5"><span className={fieldLabel} style={fieldLabelStyle}>Methodology <span style={{ color: 'var(--ink-faint)' }}>(optional)</span></span><input {...field('methodology')} className={inputCls} style={inputStyle} /></label>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className={fieldLabel} style={fieldLabelStyle}>Default tools <span style={{ color: 'var(--ink-faint)' }}>(comma-separated)</span></span>
        <input {...field('tools')} list="agent-tool-options" placeholder="get_asset_info, query_misp" className={inputCls} style={inputStyle} />
        <datalist id="agent-tool-options">{skills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</datalist>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={fieldLabel} style={fieldLabelStyle}>System prompt</span>
        <textarea {...field('prompt')} rows={10} className={promptCls} style={inputStyle} placeholder="Describe how the agent works, what it must call, and its output format…" />
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || !form.name.trim()} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Creating…' : 'Create agent'}</button>
        <button type="button" onClick={onCancel} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
      </div>
    </form>
  );
};

const AgentsDrawer = ({ open, agents, skills, workflows, initialId, nameOf, onClose, onChanged, onPickWorkflow }) => {
  const [selId, setSelId] = useState(initialId || 'containment');
  const [mode, setMode] = useState('view'); // view | new
  const [draft, setDraft] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [promptText, setPromptText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { if (open) { setMode('view'); setNotice(null); if (initialId) setSelId(initialId); } }, [open, initialId]);

  const ag = agents.find(a => a.id === selId) || agents[0];
  const custom = ag?.source === 'custom';

  useEffect(() => {
    if (!open || !ag) return;
    setPrompt(null);
    apiClient.get(`/api/workflows/agents/${ag.id}/prompt`)
      .then(res => { setPrompt(res.data?.system_prompt || ''); setPromptText(res.data?.system_prompt || ''); })
      .catch(() => { setPrompt(ag.prompt || ''); setPromptText(ag.prompt || ''); });
  }, [open, ag?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const used = ag ? workflows.filter(w => w.phases.some(p => p.agent_id === ag.id)) : [];
  const toolDesc = Object.fromEntries(skills.map(s => [s.id, s.description]));

  const savePrompt = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/api/workflows/agents/${ag.id}/prompt`, { system_prompt: promptText });
      setPrompt(promptText);
      setNotice({ kind: 'ok', text: 'Prompt saved.' });
      onChanged();
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Could not save the prompt.') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await apiClient.delete(`/api/workflows/agents/${ag.id}`);
      setSelId('triage');
      setNotice({ kind: 'ok', text: `${ag.name} deleted.` });
      onChanged();
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Could not delete the agent.') });
    } finally {
      setBusy(false);
    }
  };

  const duplicate = () => {
    setDraft({ ...EMPTY, name: `${ag.name} (custom)`, specialization: ag.specialization || '', tools: (ag.tools || []).join(', '), prompt: prompt || '' });
    setMode('new');
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="PLAYBOOKS · AGENTS"
      title={mode === 'new' ? 'New agent' : 'Agents'}
      subtitle={mode === 'new' ? 'A custom agent can be picked for any phase when you edit a playbook.' : 'Each phase runs one agent. The agent brings its system prompt and its tools; for other tools, create a custom agent.'}
    >
      {mode === 'new' ? (
        <AgentForm
          initial={draft || EMPTY}
          skills={skills}
          onCancel={() => setMode('view')}
          onSaved={(id) => { setMode('view'); if (id) setSelId(id); setNotice({ kind: 'ok', text: 'Agent created.' }); onChanged(); }}
        />
      ) : (
        <div className="flex flex-col gap-[18px]">
          <div className="flex justify-end -mt-2">
            <button type="button" onClick={() => { setDraft(null); setMode('new'); }} className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg border text-[13px] font-medium" style={secondaryBtnStyle}><Plus className="w-3.5 h-3.5" strokeWidth={2} />New agent</button>
          </div>
          {notice && (
            <div role={notice.kind === 'error' ? 'alert' : 'status'} className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={notice.kind === 'error'
              ? { borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }
              : { borderColor: 'var(--sev-good-fg)', background: 'var(--sev-good-bg)' }}>{notice.text}</div>
          )}
          <div role="group" aria-label="Choose an agent" className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {agents.map(a => {
              const on = a.id === ag?.id;
              return (
                <button key={a.id} type="button" onClick={() => { setSelId(a.id); setNotice(null); }} aria-pressed={on} className="h-11 box-border flex flex-col justify-center gap-0.5 px-2.5 rounded-[9px] border text-left" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-bg)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
                  <span className="text-[13px] font-medium truncate">{a.name}</span>
                  <span className="font-mono text-[9.5px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{a.source === 'custom' ? 'CUSTOM' : 'BUILT-IN'}</span>
                </button>
              );
            })}
          </div>

          {ag && (
            <div className="flex flex-col gap-3.5 pt-4 border-t" style={{ borderColor: 'var(--surf-line)' }}>
              <div className="flex flex-col gap-1.5">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-[17px] font-semibold">{ag.name}</span>
                  <span className="h-5 inline-flex items-center px-[7px] rounded-[5px] border font-mono text-[10px] tracking-[0.08em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>{custom ? 'CUSTOM' : 'BUILT-IN'}</span>
                  <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{ag.id}</span>
                </span>
                {ag.specialization && <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{ag.specialization}</span>}
              </div>

              <div className="flex flex-col gap-[7px]">
                <Label>SYSTEM PROMPT</Label>
                {prompt === null ? (
                  <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>Loading prompt…</span>
                ) : custom ? (
                  <>
                    <label htmlFor="agent-prompt" className="sr-only">System prompt</label>
                    <textarea id="agent-prompt" rows={10} value={promptText} onChange={e => setPromptText(e.target.value)} className={`${promptCls} resize-y`} style={inputStyle} />
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={savePrompt} disabled={busy || promptText === prompt} className="h-[38px] px-3.5 rounded-[9px] text-[13.5px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>Save prompt</button>
                      {ag.created_by && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Created by {nameOf(ag.created_by)}</span>}
                      <button type="button" onClick={() => setConfirmDelete(true)} disabled={busy} className="ml-auto h-[38px] px-2.5 text-[13px]" style={{ color: 'var(--sev-crit-fg)' }}>Delete agent</button>
                    </div>
                  </>
                ) : (
                  <>
                    <pre className="m-0 max-h-[300px] overflow-auto px-3.5 py-3 rounded-[10px] border font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{prompt || 'This agent has no stored prompt.'}</pre>
                    <div className="flex items-center gap-3">
                      <span className="flex-grow text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>Built-in prompts are read-only. Duplicate the agent to change it.</span>
                      <button type="button" onClick={duplicate} className="h-9 shrink-0 px-3 rounded-lg border text-[13px] font-medium" style={secondaryBtnStyle}>Duplicate as custom</button>
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-col gap-[7px]">
                <Label>DEFAULT TOOLS</Label>
                {ag.tools?.length ? (
                  <span className="flex flex-wrap gap-1.5">
                    {ag.tools.map(t => <span key={t} title={toolDesc[t]} className="h-6 inline-flex items-center px-2 rounded-md border font-mono text-[11.5px]" style={{ borderColor: 'var(--surf-line)' }}>{t}</span>)}
                  </span>
                ) : <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>None</span>}
                <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>Offered when you add this agent to a new phase. Each phase can narrow or change them.</span>
              </div>

              <div className="flex flex-col gap-[7px]">
                <Label>USED IN · {used.length}</Label>
                {used.length ? (
                  <span className="flex flex-wrap gap-1.5">
                    {used.map(w => <button key={w.id} type="button" onClick={() => onPickWorkflow(w.id)} className="h-7 inline-flex items-center px-2.5 rounded-[7px] border text-[12.5px] hover:border-[color:var(--surf-line-strong)]" style={{ borderColor: 'var(--surf-line)' }}>{w.name}</button>)}
                  </span>
                ) : <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Not in any playbook yet.</span>}
              </div>
            </div>
          )}
        </div>
      )}
      <ConfirmModal
        isOpen={confirmDelete}
        danger
        title={`Delete ${ag?.name}?`}
        message={used.length ? `It's used in ${used.length} playbook${used.length === 1 ? '' : 's'}. Pick another agent for those phases before deleting it.` : 'This can’t be undone.'}
        confirmLabel="Delete agent"
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </Drawer>
  );
};

export default AgentsDrawer;
